/**
 * App-level error boundary. A render throw anywhere previously whited out the
 * whole clinic UI; this catches it, reports it (server logs), and shows a
 * branded bilingual retry screen instead of a blank page.
 *
 * Class component (boundaries must be classes). Copy is static ar+en since a
 * boundary can't safely use hooks/i18n context (the tree it wraps may be the
 * thing that's broken).
 */
import { AlertTriangle } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { reportClientError } from "../lib/observability.ts";

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
	override state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	override componentDidCatch(error: Error, info: ErrorInfo): void {
		reportClientError({
			message: error.message || "render error",
			stack: `${error.stack ?? ""}\n--- componentStack ---${info.componentStack ?? ""}`,
			source: "error_boundary",
		});
	}

	override render(): ReactNode {
		if (!this.state.hasError) return this.props.children;
		return (
			<div className="min-h-dvh flex items-center justify-center bg-muted p-6">
				<div className="w-full max-w-sm text-center rounded-2xl border border-border bg-card p-8 shadow-card">
					<div className="mx-auto mb-4 flex size-14 items-center justify-center rounded-full bg-red-50 dark:bg-red-950/40 text-red-600 dark:text-red-400 text-2xl">
						<AlertTriangle className="size-7" strokeWidth={2} aria-hidden />
					</div>
					<h1 className="text-lg font-bold text-foreground">
						صار خطأ غير متوقّع
					</h1>
					<p className="mt-1 text-sm text-muted-foreground">
						Something went wrong
					</p>
					<p className="mt-3 text-sm text-muted-foreground">
						حدث خطأ بالتطبيق. جرّب إعادة التحميل — تم تسجيل المشكلة تلقائياً.
					</p>
					<button
						type="button"
						onClick={() => window.location.reload()}
						className="mt-5 inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
					>
						إعادة التحميل / Reload
					</button>
				</div>
			</div>
		);
	}
}
